/**
 * Domain types for the client directory and per-client capabilities.
 */

export type ClientCapability =
  | "view_jobs"
  | "create_work_orders"
  | "create_quote_requests";

export const CLIENT_CAPABILITIES: readonly { value: ClientCapability; label: string; description: string }[] = [
  {
    value: "view_jobs",
    label: "View jobs",
    description: "Can see jobs associated with this client organization.",
  },
  {
    value: "create_work_orders",
    label: "Create work orders",
    description: "Can raise work-order requests on behalf of this client.",
  },
  {
    value: "create_quote_requests",
    label: "Create quote requests",
    description: "Can raise quote requests on behalf of this client.",
  },
];

/** A lightweight client entry for autocomplete result rows. */
export interface ClientSuggestion {
  id: string;
  name: string;
  detail?: string;
}

/** Full client record returned by the management API. */
export interface ClientRecord extends ClientSuggestion {
  slug: string;
  phone: string;
  email: string;
  address: string;
  notes: string;
  status: string;
  abnNumber: string;
  acnNumber: string;
  /** Client type: company, sole trader, partnership, trust, or person. */
  type: string;
  createdAt: string;
  updatedAt: string | null;
  capabilities: ClientCapability[];
  /** Live contacts, newest-name-ordered; empty until the server populates it. */
  contacts: ClientContact[];
}

/** A client contact. */
export interface ClientContact {
  id: string;
  clientId: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes: string;
  displayName: string;
  createdAt: string;
  updatedAt: string | null;
}

/** The shape submitted by the inline create-client form. `legalName` required. */
export interface CreateClientDraft {
  legalName: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  abnNumber?: string;
  acnNumber?: string;
  type?: string;
}

/** The shape submitted by the inline create-contact form. */
export interface CreateContactDraft {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  notes?: string;
}
